import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { Problem, SearchConfig, Stack, SubjectPageItem, ThemeAttribute } from '@yandex-int/k-common/typings';
//@ts-ignore
import { getActualThemeId } from '@yandex-int/k-common/utils/get-actual-theme-id';
import { uniqBy } from 'lodash';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import {
    getSubjectPages as getSubjectPagesSelector,
    getExperimentalSubjectPages as getExperimentalSubjectPagesSelector,
    getFeatureFlags,
} from 'platform-components/src/components/app-factory/selectors';
import { createCachedSelector } from 're-reselect';
import { createSelector } from 'reselect';

import { getLessonProblems } from 'common.components/lesson/selectors';
import { getFirstActiveTeamId, getIsInShowcaseExperiment } from 'common.components/user/selectors';
import { SECONDARY_SCHOOL_FIRST_CLASS } from 'common.components/user/user.constants';
import { getCoursesTemplatesList, getCoursesTemplatesLoadingStatus } from 'store/courses-templates/selectors';
import { getCoursesLoadingStatus, getSubjects as getSubjectsByClass } from 'store/courses/selectors';
import { subjectsOrderSort } from 'store/courses/utils';
import { getSubjectConfigList } from 'store/subject-config/selectors';
import * as themesSelector from 'store/themes/selectors';
import {
    getGradeFromProps,
    getSubjectFromProps,
    getThemeIdFromProps,
    PropsWithGrade,
    PropsWithSubject,
} from 'utils/selectors/selectors';

import type { ReduxTeacherStore } from '../../typings';
import type { LibraryStoreState } from '../library.common/typings';

interface PropsWithThemeId {
    themeId: number;
}

interface PropsWithStack {
    stack: Stack;
}

const librarySelector = (storeState: ReduxTeacherStore): LibraryStoreState => storeState?.library;

const getStackFromProps = (_storeState: ReduxTeacherStore, ownProps: PropsWithStack): Stack | null => {
    return ownProps?.stack || null;
};

const getCoursesPreview = createSelector(librarySelector, (library) => library.coursesPreview);

export const getPreviewSubjectLists = createSelector(getCoursesPreview, (coursesPreview) =>
    coursesPreview.map(({ subject, grade, name, course_container_info }) => ({
        subject,
        grade,
        name,
        slugs: course_container_info.map((c) => c.slug),
    }))
);

export const getPreviewCourseContainers = createSelector(
    [getCoursesPreview, getSubjectFromProps, getGradeFromProps],
    (coursesPreview, subject, grade) => {
        const current = coursesPreview.find((c) => c.subject === subject && c.grade === grade);

        return current?.course_container_info.map(({ slug }) => ({
            container: {
                slug: slug,
                cover: null,
                color: null,
                issue_date: null, // "2023-2024",
                cover_background_color: null,
            },
            module_lesson_link: {
                lesson: null,
                module: null,
                group_name: null,
                order: null,
            },
            course_container_link: {
                study_year: null, // "2023-2024"
            },
        }));
    }
);

export const getSubjectLists = createSelector(
    [getCoursesTemplatesList, getFirstActiveTeamId, getSubjectsByClass, getSubjectConfigList],
    (courseTemplates, firstActiveTeamId, teamSubjects, subjectConfig) => {
        if (firstActiveTeamId) {
            return teamSubjects || [];
        }

        return subjectsOrderSort(
            uniqBy(
                courseTemplates.map((template) => ({ subject: template.subject, name: template.name })) || [],
                'subject'
            ),
            subjectConfig
        );
    }
);

export const getIsSubjectListLoading = createSelector(
    [getFirstActiveTeamId, getCoursesLoadingStatus, getCoursesTemplatesLoadingStatus],
    (firstActiveTeamId, coursesLoadingStatus, courseTemplatesLoadingStatus) => {
        if (firstActiveTeamId) {
            return (
                coursesLoadingStatus === LOADING_STATUSES.LOADING || coursesLoadingStatus === LOADING_STATUSES.UNSENT
            );
        }

        return courseTemplatesLoadingStatus === LOADING_STATUSES.LOADING;
    }
);

export const getSubjects = createSelector([getSubjectsByClass], (subjects) => {
    if (!subjects) {
        return [];
    }

    return subjects.map((c) => c.subject);
});

export const getProblemsLoadingStatus = createSelector(librarySelector, (state) =>
    get(state, 'stackProblemsLoadingStatus')
);

const isPrimarySchool = (grade?: number | null) => Boolean(grade && grade < SECONDARY_SCHOOL_FIRST_CLASS);

const getKey = (page: SubjectPageItem): string => `${page.subject}#${page.grade}`;

const getSubjectPagesHash = (pages?: Array<SubjectPageItem>): Record<string, SubjectPageItem> => {
    if (!pages) {
        return {};
    }

    return pages.reduce((result: Record<string, SubjectPageItem>, page) => {
        result[getKey(page)] = page;

        return result;
    }, {});
};

export const getSubjectPagesConfig = createSelector(
    [getSubjectPagesSelector, getExperimentalSubjectPagesSelector, getIsInShowcaseExperiment],
    (commonSourcePages, experimentalSourcePages, isInExperiment) => {
        if (isInExperiment) {
            const experimentalHash = getSubjectPagesHash(experimentalSourcePages?.pages);

            return (commonSourcePages?.pages || []).map((page: SubjectPageItem) => {
                const key = getKey(page);

                if (experimentalHash[key]) {
                    return experimentalHash[key];
                }

                return page;
            });
        }

        return commonSourcePages?.pages || [];
    }
);

export const getSubjectPages = createCachedSelector(
    [getSubjectPagesConfig, getSubjectFromProps, getGradeFromProps],
    (sourcePages, currentSubject, currentGrade) => {
        const pages =
            sourcePages?.filter(({ subject, grade }) => {
                const isGradeOk = grade === currentGrade || (isPrimarySchool(grade) && isPrimarySchool(currentGrade));

                return subject === currentSubject && isGradeOk;
            }) || [];
        pages.sort(({ grade: gradeA }, { grade: gradeB }) => gradeA - gradeB);

        return pages;
    }
)((state, props: PropsWithSubject & PropsWithGrade) => {
    return `${props.subject}:${props.grade}`;
});

export const getCurrentSubjectPage = createSelector(
    [getSubjectPages, getGradeFromProps],
    (subjectPages, grade): SubjectPageItem | null => {
        return (grade && subjectPages.find((item) => item.grade === grade)) || null;
    }
);

export const getCurrentSubjectPageSlug = createSelector([getCurrentSubjectPage], (page): string | null => {
    if (!page) {
        return null;
    }
    return page.slug;
});

export const getCurrentShowcaseSlug = createSelector(
    [getSubjectPagesSelector, getExperimentalSubjectPagesSelector, getIsInShowcaseExperiment],
    (commonSourcePages, experimentalSourcePages, isInExperiment): string | null => {
        if (isInExperiment) {
            return experimentalSourcePages?.slug || null;
        }

        return commonSourcePages?.slug || null;
    }
);

export const getProblemCountInCurrentLesson = createSelector(
    [getLessonProblems, getStackFromProps],
    (lessonProblems, stack) => {
        return lessonProblems.reduce((count: number, problem) => {
            return count + (stack?.id === problem?.from_theme ? 1 : 0);
        }, 0);
    }
);

export const getStacks = createSelector(
    [librarySelector, getThemeIdFromProps, getProblemsLoadingStatus],
    (state, themeId, loadingStatus): Array<Stack> => {
        const stacks: Array<Stack> = get(state, `stacks.${themeId}`, []);

        if (loadingStatus !== LOADING_STATUSES.SUCCESS) {
            return stacks;
        }

        return stacks.filter((stack) => Boolean(stack.representative));
    }
);

export const getStacksLoadingStatus = createSelector(librarySelector, (state) => get(state, 'stacksLoadingStatus'));

export const getLastPageLoaded = (storeState: ReduxTeacherStore, themeId: number) => {
    const state = librarySelector(storeState);

    return get(state, `stacksLoadedPages.${themeId}`);
};

export const getFiltersByTheme = (
    storeState: ReduxTeacherStore,
    ownProps: PropsWithThemeId & PropsWithSubject & PropsWithGrade
): Array<ThemeAttribute> => {
    const { themeId, ...restProps } = ownProps;
    const isActualTheme = themesSelector.getIsActualTheme(storeState, { themeId });
    let theme;

    if (isActualTheme) {
        theme = themesSelector.getThemeById(storeState, {
            themeId: getActualThemeId(themeId),
            ...restProps,
        });
    } else {
        theme = themesSelector.getThemeById(storeState, ownProps);
    }

    if (!theme) {
        return [];
    }
    const whiteList = theme?.filters_white_list || null;
    const library = librarySelector(storeState);
    const attributes: Array<ThemeAttribute> = library.attributes[themeId] || [];

    if (whiteList && whiteList.length) {
        return attributes.filter((attr) => whiteList.some((item) => item === attr.slug));
    }

    return attributes;
};

export const getAvailableFiltersByTheme = createSelector(
    [librarySelector, getThemeIdFromProps],
    (state: LibraryStoreState, themeId: number): Array<ThemeAttribute> => {
        if (state?.availableAttributes && themeId) {
            return state?.availableAttributes[themeId] || [];
        }

        return [];
    }
);

export const getFilterValues = createSelector(
    librarySelector,
    (state: LibraryStoreState): Record<string, Array<string> | string> => {
        return get(state, 'filterValues');
    }
);

export const getShouldShowClearButton = createSelector(
    getFilterValues,
    (filterValues: Record<string, Array<string> | string>): boolean => {
        return Object.values(filterValues).some((value) => !isNil(value));
    }
);

export const getSelectedStackId = createSelector(librarySelector, (state) => get(state, 'selectedStackId'));

export const getThemeDetails = createSelector([librarySelector, getThemeIdFromProps], (state, themeId) => {
    if (state?.themesDetails && themeId) {
        return state.themesDetails[themeId] || null;
    }

    return null;
});

export const getThemeDetailsLoadingStatus = createCachedSelector(
    [librarySelector, getThemeIdFromProps],
    (state, themeId) => {
        if (state?.themeDetailsLoadingStatus && themeId) {
            return state.themeDetailsLoadingStatus[themeId] || LOADING_STATUSES.UNSENT;
        }

        return LOADING_STATUSES.UNSENT;
    }
)((state, props: PropsWithThemeId) => {
    return props.themeId.toString();
});

export const getCanLoadMoreStacks = (storeState: ReduxTeacherStore, themeId: number) => {
    const state = librarySelector(storeState);

    return get(state, `canLoadMoreStacks.${themeId}`, true);
};

export const getIsProblemLoading = (storeState: ReduxTeacherStore, representative: Problem) => {
    const loadingStatus = getProblemsLoadingStatus(storeState);

    return !representative && loadingStatus === LOADING_STATUSES.LOADING;
};

export const getProblemIdsInLesson = createSelector([getLessonProblems], (problemBlocksInLesson) => {
    return problemBlocksInLesson.map((block) => block?.problem?.id);
});

export const getShouldShowFilters = (
    storeState: ReduxTeacherStore,
    ownProps: PropsWithThemeId & PropsWithGrade & PropsWithSubject
): boolean => {
    const { themeId, ...restProps } = ownProps;
    const isActualTheme = themesSelector.getIsActualTheme(storeState, ownProps);

    if (isActualTheme) {
        const selectedActualTheme = themesSelector.getThemeById(storeState, {
            ...restProps,
            themeId: getActualThemeId(themeId),
        });

        return Boolean(selectedActualTheme);
    }

    const selectedTheme = themesSelector.getThemeById(storeState, ownProps);

    return Boolean(selectedTheme && selectedTheme.parent);
};

export const getIsThemeEmpty = createSelector(getThemeDetails, (themeDetails) => {
    const lessonCount = get(themeDetails, 'lessons_count', 0);
    const stacksCount = get(themeDetails, 'stacks_count', 0);

    return !lessonCount && !stacksCount;
});

export const getDeepLinkStack = createSelector(librarySelector, (library) => library.deepLinkStack);

export const getSearchConfig = createSelector(
    [getSubjectFromProps, getGradeFromProps, getSubjectConfigList],
    (currentSubject, grade, subjectConfig): SearchConfig | null => {
        const searchNamespaceByGrade = subjectConfig.find(({ subject }) => subject === currentSubject)
            ?.search_namespace_by_grade;

        return searchNamespaceByGrade?.find((item) => item.grade === grade) || null;
    }
);

export const getShouldShowLibrarySearch = createSelector(
    [getSubjectFromProps, getFeatureFlags, getSearchConfig],
    (currentSubject, featureFlags, searchConfig): boolean => {
        return Boolean(featureFlags?.librarySearch && searchConfig && searchConfig.namespace_ids.length !== 0);
    }
);
